import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { SystemConfig } from '@/interfaces/systemConfig.interface';
import { getSystemConfig } from '@/api/system.api';
import { setSystemConfig } from '@/api/base.api';

interface SystemConfigContextType {
  systemConfig: SystemConfig | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const SystemConfigContext = createContext<SystemConfigContextType | undefined>(
  undefined
);

export const SystemConfigProvider = ({ children }: { children: ReactNode }) => {
  const [systemConfig, setLocalSystemConfig] = useState<SystemConfig | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSystemConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const config = await getSystemConfig();
      setLocalSystemConfig(config);
      setSystemConfig(config);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to fetch system configuration')
      );
      setLocalSystemConfig(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemConfig();
  }, []);

  return (
    <SystemConfigContext.Provider
      value={{
        systemConfig,
        loading,
        error,
        refetch: fetchSystemConfig,
      }}
    >
      {children}
    </SystemConfigContext.Provider>
  );
};

export const useSystemConfig = () => {
  const context = useContext(SystemConfigContext);
  if (context === undefined) {
    throw new Error(
      'useSystemConfig must be used within a SystemConfigProvider'
    );
  }
  return context;
};

/**
 * System config when a provider is present, `undefined` when it is not.
 *
 * For features that improve with the config but must not fail without it — the
 * import flow's schema check reads the graph engine type from here, and an
 * import has to work whether or not it can.
 */
export const useOptionalSystemConfig = () => useContext(SystemConfigContext);
